import React, { useMemo, useState, useEffect } from 'react'
import { useLocation } from 'wouter'
import { useKPIs, useTrendData, useFiscalWeeks, useCalendarWeeks, useOnlineTopSKUs, useStoreTopSKUs, useTopStoreLocations, useKPIRevenueDetail, useKPIUnitsDetail, useKPISKUsDetail } from '../lib/data-service'
import RevenueTrendChart from '../components/RevenueTrendChart'
import { useFiscalWeekContext } from '../contexts/FiscalWeekContext'
import { useTenant } from '../contexts/TenantContext'
import { getRetailerInfo } from '../config/retailers'
import GlobalHeader from '../components/GlobalHeader'
import HeroKpiCard from '../components/HeroKpiCard'
import SectionHeader from '../components/SectionHeader'
import InventoryCoverageTile from '../components/InventoryCoverageTile'
import StoreOOSTile from '../components/StoreOOSTile'
import TopRevenueConcentrationTile from '../components/TopRevenueConcentrationTile'
import AIInsightsPanel from '../components/AIInsightsPanel'
import { Channel, FiscalWeek } from '../../../shared/schema'
import { parseFiscalWeek } from '../../../shared/analytics'
import { 
  TrendingUp, 
  TrendingDown,
  Users, 
  DollarSign, 
  Package, 
  Store, 
  ShoppingCart,
  Calendar,
  Filter,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Globe,
  BarChart3,
  MapPin,
  X,
  ExternalLink,
  Trophy,
  Rocket
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../lib/api-client'

// Performance Highlights hook
function usePerformanceHighlights(fiscalWeek?: number) {
  return useQuery({
    queryKey: ['performance-highlights', fiscalWeek],
    queryFn: () => {
      const params = new URLSearchParams()
      if (fiscalWeek) {
        params.append('fiscalWeek', fiscalWeek.toString())
      }
      const queryString = params.toString()
      return apiClient.get<any>(`/dashboard/performance-highlights${queryString ? `?${queryString}` : ''}`)
    },
    enabled: !!fiscalWeek,
    staleTime: 5 * 60 * 1000,
  })
}

export default function Dashboard() {
  const { selectedFiscalWeek, setSelectedFiscalWeek, setSelectedDateRange } = useFiscalWeekContext()
  const { selectedRetailerId, setSelectedRetailerId } = useTenant()
  const [, setLocation] = useLocation()
  const [isLoading, setIsLoading] = useState(false)
  const [showRevenueSkus, setShowRevenueSkus] = useState(false)
  const [showUnitsSkus, setShowUnitsSkus] = useState(false)
  const [showStoreRevenueSkus, setShowStoreRevenueSkus] = useState(false)
  const [showTopStores, setShowTopStores] = useState(false)
  const [showStoreSkus, setShowStoreSkus] = useState(false)
  const [activeModal, setActiveModal] = useState<'revenue' | 'units' | 'skus' | 'stores' | null>(null)
  const [trendWindow, setTrendWindow] = useState<4 | 8 | 12>(8)

  // Data fetching
  const { data: kpis, isLoading: kpisLoading, error: kpisError, refetch: refetchKPIs } = useKPIs(selectedFiscalWeek)
  const { data: fiscalWeeks } = useFiscalWeeks()
  const { data: calendarData } = useCalendarWeeks()
  const { data: performanceHighlights } = usePerformanceHighlights(selectedFiscalWeek)

  // Reset fiscal week when retailer changes to avoid loading weeks that don't exist for that retailer
  useEffect(() => {
    if (fiscalWeeks && fiscalWeeks.length > 0) {
      // If selected week is undefined or doesn't exist in available weeks for this retailer, reset to latest
      const weekExists = selectedFiscalWeek && fiscalWeeks.some(w => w.value === selectedFiscalWeek)
      if (!weekExists) {
        const newFw = fiscalWeeks[0].value
        console.log(`[Dashboard] Selected week ${selectedFiscalWeek} not available for retailer, resetting to latest: ${newFw}`)
        setSelectedFiscalWeek(newFw)
        // Also sync selectedDateRange so the CalendarWeekPicker stays in sync
        if (calendarData?.weeks) {
          const fyStr = 'FY' + String(Math.floor(newFw / 100))
          const fwNum = newFw % 100
          const match = calendarData.weeks.find(w => w.fiscal_year === fyStr && w.fiscal_week === fwNum)
          if (match) {
            setSelectedDateRange({ start: match.start_date, end: match.end_date })
          }
        }
      }
    }
  }, [selectedRetailerId, fiscalWeeks, selectedFiscalWeek, setSelectedFiscalWeek, calendarData, setSelectedDateRange])
  const fiscalWeekValues = useMemo(() => fiscalWeeks?.map(week => week.value) ?? [], [fiscalWeeks])
  const selectedWeekIndex = useMemo(() => {
    if (!selectedFiscalWeek) return 0
    const idx = fiscalWeekValues.findIndex(week => week === selectedFiscalWeek)
    return idx >= 0 ? idx : 0
  }, [selectedFiscalWeek, fiscalWeekValues])

  const trendWeeks = useMemo(() => {
    if (fiscalWeekValues.length === 0) return []
    // Fiscal weeks are sorted DESC (newest first)
    // Start from selected week and take trendWindow weeks forward in the array (which goes back in time)
    const start = selectedWeekIndex
    const end = Math.min(fiscalWeekValues.length, start + trendWindow)
    const slice = fiscalWeekValues.slice(start, end)
    // Reverse to show oldest to newest on chart
    return slice.length > 0 ? [...slice].reverse() : []
  }, [fiscalWeekValues, selectedWeekIndex, trendWindow])

  const { data: trendData, isLoading: trendLoading } = useTrendData({
    weeks: trendWeeks,
    window: trendWindow
  })
  
  // Online SKU data
  const { data: topSkusByRevenue } = useOnlineTopSKUs(selectedFiscalWeek, 'revenue', 5)
  const { data: topSkusByUnits } = useOnlineTopSKUs(selectedFiscalWeek, 'units', 5)
  
  // Store performance data
  const { data: topStoreSkusByRevenue } = useStoreTopSKUs(selectedFiscalWeek, 'revenue', 5)
  const { data: topStoreLocations } = useTopStoreLocations(selectedFiscalWeek, 5)
  
  // KPI detail data for modals
  const { data: revenueDetail } = useKPIRevenueDetail(selectedFiscalWeek, 5)
  const { data: unitsDetail } = useKPIUnitsDetail(selectedFiscalWeek, 5)
  const { data: skusDetail } = useKPISKUsDetail(selectedFiscalWeek, 5)

  const handleRefresh = async () => {
    setIsLoading(true)
    await refetchKPIs()
    setIsLoading(false)
  }

  const navigateToDeepDive = (type: 'revenue' | 'units' | 'skus' | 'stores') => {
    setActiveModal(null)
    const routes = {
      revenue: '/revenue-detail',
      units: '/units-detail', 
      skus: '/skus-detail',
      stores: '/stores'
    }
    setLocation(routes[type])
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value)
  }

  const formatPercentage = (value: number) => {
    return `${value >= 0 ? '+' : ''}${(value * 100).toFixed(1)}%`
  }

  const formatChangeChip = (value: number | null | undefined, label: string) => {
    if (value === null || value === undefined) return undefined
    return `${formatPercentage(value)} ${label}`
  }

  const getTone = (value: number | null | undefined): 'success' | 'danger' | 'neutral' => {
    if (value === null || value === undefined) return 'neutral'
    return value >= 0 ? 'success' : 'danger'
  }

  const getTrendIcon = (value: number) => {
    return value >= 0 ? (
      <TrendingUp className="w-4 h-4 text-green-500" />
    ) : (
      <TrendingDown className="w-4 h-4 text-red-500" />
    )
  }

  const getTrendColor = (value: number) => {
    return value >= 0 ? 'text-green-500' : 'text-red-500'
  }

  if (kpisError) {
    const retailerInfo = selectedRetailerId ? getRetailerInfo(selectedRetailerId) : null;
    
    return (
      <div className="min-h-screen bg-gray-50">
        <GlobalHeader 
          title="PatternIQ Dashboard"
          subtitle="Real-time retail performance analytics and AI-powered insights"
          onRefresh={handleRefresh}
          isLoading={false}
        />
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center border-2 border-gray-200">
            <div 
              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ 
                backgroundColor: retailerInfo?.colors.light || '#F3F4F6',
                border: `3px solid ${retailerInfo?.colors.medium || '#E5E7EB'}`
              }}
            >
              <Store 
                className="w-10 h-10"
                style={{ color: retailerInfo?.colors.primary || '#6B7280' }}
              />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              {retailerInfo ? `No ${retailerInfo.name} Data Available` : 'No Data Available'}
            </h2>
            
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {retailerInfo 
                ? `We haven't received any data for ${retailerInfo.name} yet. Upload data to start seeing insights and analytics for this retail partner.`
                : 'There is no data available for this view. Please try selecting a different retailer or contact support.'
              }
            </p>

            <div className="flex items-center justify-center space-x-4">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  setLocation('/dashboard');
                }}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                ← Back to Overview
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  handleRefresh();
                }}
                className="px-6 py-3 rounded-lg transition-colors font-medium text-white"
                style={{ 
                  backgroundColor: retailerInfo?.colors.primary || '#3B82F6'
                }}
              >
                Retry
              </button>
            </div>

            {retailerInfo && (
              <div className="mt-8 pt-8 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  💡 Upload {retailerInfo.name} data via{' '}
                  <button 
                    onClick={() => setLocation('/admin/data-upload')}
                    className="text-indigo-600 hover:text-indigo-700 font-medium underline"
                  >
                    Data Upload
                  </button>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Global Header */}
      <GlobalHeader 
        title="PatternIQ Dashboard"
        subtitle="Real-time retail performance analytics and AI-powered insights"
        onRefresh={handleRefresh}
        isLoading={isLoading}
      />

      {/* Main Content Container */}
      <div className="max-w-7xl mx-auto px-8 py-6 space-y-6">
        
        {/* Headline KPIs - Traditional 4-column horizontal row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Total Revenue */}
                  <HeroKpiCard
                    label="Total Revenue"
                    value={kpisLoading ? '...' : formatCurrency(kpis?.totalRevenue || 0)}
                    icon={DollarSign}
                    yoyDeltaText={kpis?.yoyGrowth !== undefined ? `${formatPercentage(kpis.yoyGrowth)} vs LY` : undefined}
                    yoyDeltaTone={kpis?.yoyGrowth !== undefined ? (kpis.yoyGrowth >= 0 ? 'success' : 'danger') : 'neutral'}
                    wowDeltaText={formatChangeChip(kpis?.weekOverWeek?.revenue ?? null, 'vs LW')}
                    wowDeltaTone={getTone(kpis?.weekOverWeek?.revenue ?? null)}
                    onClick={() => setActiveModal('revenue')}
                    askAgent={{
                      metric: 'revenue',
                      value: kpis?.totalRevenue,
                      delta: kpis?.yoyGrowth,
                      week: selectedFiscalWeek?.toString(),
                      retailerId: selectedRetailerId
                    }}
                  />
                  {/* Units Sold */}
                  <HeroKpiCard
                    label="Units Sold"
                    value={kpisLoading ? '...' : formatNumber(kpis?.totalUnits || 0)}
                    icon={ShoppingCart}
                    yoyDeltaText={kpis?.units_change !== undefined && kpis?.units_change !== null ? `${formatPercentage(kpis.units_change)} vs LY` : undefined}
                    yoyDeltaTone={kpis?.units_change !== undefined && kpis?.units_change !== null ? (kpis.units_change >= 0 ? 'success' : 'danger') : 'neutral'}
                    wowDeltaText={formatChangeChip(kpis?.weekOverWeek?.units ?? null, 'vs LW')}
                    wowDeltaTone={getTone(kpis?.weekOverWeek?.units ?? null)}
                    onClick={() => setActiveModal('units')}
                    askAgent={{
                      metric: 'units',
                      value: kpis?.totalUnits,
                      delta: kpis?.units_change,
                      week: selectedFiscalWeek?.toString(),
                      retailerId: selectedRetailerId
                    }}
                  />
                  {/* Active SKUs */}
                  <HeroKpiCard
                    label="Active SKUs"
                    value={kpisLoading ? '...' : formatNumber(kpis?.activeSKUs || 0)}
                    icon={Package}
                    yoyDeltaText={undefined}
                    yoyDeltaTone="neutral"
                    wowDeltaText={formatChangeChip(kpis?.weekOverWeek?.activeSKUs ?? null, 'vs LW')}
                    wowDeltaTone={getTone(kpis?.weekOverWeek?.activeSKUs ?? null)}
                    onClick={() => setActiveModal('skus')}
                    askAgent={{
                      metric: 'inventory',
                      week: selectedFiscalWeek?.toString(),
                      retailerId: selectedRetailerId
                    }}
                  />
          {/* Store Count */}
          <HeroKpiCard
            label="Store Count"
            value={kpisLoading ? '...' : formatNumber(kpis?.storeCount || 0)}
            icon={Store}
            yoyDeltaText="Active locations"
            yoyDeltaTone="neutral"
            wowDeltaText={formatChangeChip(kpis?.weekOverWeek?.storeCount ?? null, 'vs LW')}
            wowDeltaTone={getTone(kpis?.weekOverWeek?.storeCount ?? null)}
            onClick={() => setActiveModal('stores')}
            askAgent={{
              metric: 'sales',
              week: selectedFiscalWeek?.toString(),
              retailerId: selectedRetailerId
            }}
          />
        </div>
        {/* End KPI Row */}
        
        {/* Revenue Trend - Full Width */}
        <section className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 px-6 py-3">
            <div className="flex items-center space-x-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-bold text-gray-900">Revenue Trend</h2>
            </div>
          </div>
          <div className="p-6">
            <RevenueTrendChart
              data={trendData}
              selectedWindow={trendWindow}
              onWindowChange={setTrendWindow}
              className={trendLoading ? 'opacity-60 animate-pulse' : ''}
            />
          </div>
        </section>

        {/* Channel Performance - Full Width */}
        <section className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-b border-gray-200 px-6 py-3">
            <div className="flex items-center space-x-2">
              <Users className="w-5 h-5 text-purple-600" />
              <h2 className="text-lg font-bold text-gray-900">Channel Mix</h2>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 gap-6">
                
                {/* Online Sales (Ulta.com) Card */}
                <div className="bg-gradient-to-br from-purple-50 to-white border border-purple-100 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="p-1.5 bg-purple-100 rounded-md">
                      <Globe className="w-4 h-4 text-purple-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 text-sm">Online Sales</h4>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {/* Total Revenue */}
                    <div 
                      className="bg-white border border-purple-200 rounded-lg p-3 cursor-pointer hover:border-purple-300 hover:shadow-md transition-all duration-200"
                      onClick={() => setShowRevenueSkus(!showRevenueSkus)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <DollarSign className="w-4 h-4 text-purple-600" />
                        <ChevronDown className={`w-3.5 h-3.5 text-purple-600 transition-transform duration-200 ${showRevenueSkus ? 'rotate-180' : ''}`} />
                      </div>
                      <div className="text-2xl font-bold text-gray-900 mb-1">
                        {(kpis?.onlineRevenue || 0) > 0 ? formatCurrency(kpis.onlineRevenue) : <span className="text-gray-400">N/A</span>}
                      </div>
                      <div className="text-xs text-gray-600 mb-1">Total Revenue</div>
                      <div className="text-xs text-gray-400 font-medium italic">Split not provided</div>
                      <div className="text-xs text-purple-600 font-medium flex items-center gap-1 hidden">
                        {formatPercentage((kpis?.onlineRevenue || 0) / (kpis?.totalRevenue || 1))} of total
                        {formatChangeChip(kpis?.weekOverWeek?.onlineRevenue ?? null, 'vs LW') && (
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md border text-[10px] font-semibold ${getTone(kpis?.weekOverWeek?.onlineRevenue ?? null) === 'success' ? 'bg-green-50 text-green-600 border-green-200' : getTone(kpis?.weekOverWeek?.onlineRevenue ?? null) === 'danger' ? 'bg-red-50 text-red-600 border-red-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                            {formatChangeChip(kpis?.weekOverWeek?.onlineRevenue ?? null, 'vs LW')}
                          </span>
                        )}
                      </div>
                      
                      {/* Expandable Top SKUs */}
                      {showRevenueSkus && (
                        <div className="mt-3 pt-3 border-t border-purple-100">
                          <div className="text-xs font-semibold text-purple-700 mb-2">TOP SKUs</div>
                          <div className="space-y-1.5">
                            {(topSkusByRevenue as any)?.products?.slice(0, 3).map((sku: any, index: number) => (
                              <div key={sku.sku} className="flex items-center justify-between text-xs">
                                <span className="text-gray-700 truncate flex-1 mr-2" title={sku.name}>
                                  {index + 1}. {sku.name.length > 25 ? sku.name.substring(0, 25) + '...' : sku.name}
                                </span>
                                <span className="font-semibold text-gray-900 text-nowrap">{formatCurrency(sku.revenue)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Total Units */}
                    <div 
                      className="bg-white border border-purple-200 rounded-lg p-3 cursor-pointer hover:border-purple-300 hover:shadow-md transition-all duration-200"
                      onClick={() => setShowUnitsSkus(!showUnitsSkus)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <ShoppingCart className="w-4 h-4 text-purple-600" />
                        <ChevronDown className={`w-3.5 h-3.5 text-purple-600 transition-transform duration-200 ${showUnitsSkus ? 'rotate-180' : ''}`} />
                      </div>
                      <div className="text-2xl font-bold text-gray-900 mb-1">
                        {(kpis?.onlineUnits || 0) > 0 ? formatNumber(kpis.onlineUnits) : <span className="text-gray-400">N/A</span>}
                      </div>
                      <div className="text-xs text-gray-600 mb-1">Total Units</div>
                      <div className="text-xs text-gray-400 font-medium flex items-center gap-1 italic">
                        Split not provided
                        {formatChangeChip(kpis?.weekOverWeek?.onlineUnits ?? null, 'vs LW') && (
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md border text-[10px] font-semibold ${getTone(kpis?.weekOverWeek?.onlineUnits ?? null) === 'success' ? 'bg-green-50 text-green-600 border-green-200' : getTone(kpis?.weekOverWeek?.onlineUnits ?? null) === 'danger' ? 'bg-red-50 text-red-600 border-red-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                            {formatChangeChip(kpis?.weekOverWeek?.onlineUnits ?? null, 'vs LW')}
                          </span>
                        )}
                      </div>
                      
                      {/* Expandable Top SKUs */}
                      {showUnitsSkus && (
                        <div className="mt-3 pt-3 border-t border-purple-100">
                          {(topSkusByUnits as any)?.products?.length > 0 && (topSkusByUnits as any).products.some((p: any) => p.units > 0) ? (
                            <>
                              <div className="text-xs font-semibold text-purple-700 mb-2">TOP SKUs</div>
                              <div className="space-y-1.5">
                                {(topSkusByUnits as any)?.products?.filter((p: any) => p.units > 0).slice(0, 3).map((sku: any, index: number) => (
                                  <div key={sku.sku} className="flex items-center justify-between text-xs">
                                    <span className="text-gray-700 truncate flex-1 mr-2" title={sku.name}>
                                      {index + 1}. {sku.name.length > 25 ? sku.name.substring(0, 25) + '...' : sku.name}
                                    </span>
                                    <span className="font-semibold text-gray-900 text-nowrap">{formatNumber(sku.units)}</span>
                                  </div>
                                ))}
                              </div>
                            </>
                          ) : (
                            <div className="text-xs text-gray-500 italic">N/A - Online/store split not available</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* In Store Performance Card */}
                <div className="bg-gradient-to-br from-green-50 to-white border border-green-100 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="p-1.5 bg-green-100 rounded-md">
                      <Store className="w-4 h-4 text-green-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 text-sm">In Store Performance</h4>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {/* Top SKUs */}
                    <div 
                      className="bg-white border border-green-200 rounded-lg p-3 cursor-pointer hover:border-green-300 hover:shadow-md transition-all duration-200"
                      onClick={() => setShowStoreSkus(!showStoreSkus)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Package className="w-4 h-4 text-green-600" />
                        <ChevronDown className={`w-3.5 h-3.5 text-green-600 transition-transform duration-200 ${showStoreSkus ? 'rotate-180' : ''}`} />
                      </div>
                      <div className="text-2xl font-bold text-gray-900 mb-1">
                        {formatCurrency(kpis?.storeRevenue || 0)}
                      </div>
                      <div className="text-xs text-gray-600 mb-1">Top SKUs</div>
                      <div className="text-xs text-green-600 font-medium flex items-center gap-1">
                        Store revenue
                        {formatChangeChip(kpis?.weekOverWeek?.storeRevenue ?? null, 'vs LW') && (
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md border text-[10px] font-semibold ${getTone(kpis?.weekOverWeek?.storeRevenue ?? null) === 'success' ? 'bg-green-50 text-green-600 border-green-200' : getTone(kpis?.weekOverWeek?.storeRevenue ?? null) === 'danger' ? 'bg-red-50 text-red-600 border-red-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                            {formatChangeChip(kpis?.weekOverWeek?.storeRevenue ?? null, 'vs LW')}
                          </span>
                        )}
                      </div>
                      
                      {/* Expandable Top SKUs */}
                      {showStoreSkus && (
                        <div className="mt-3 pt-3 border-t border-green-100">
                          <div className="text-xs font-semibold text-green-700 mb-2">TOP SKUs</div>
                          <div className="space-y-1.5">
                            {(topStoreSkusByRevenue as any)?.products?.slice(0, 3).map((sku: any, index: number) => (
                              <div key={sku.sku} className="flex items-center justify-between text-xs">
                                <span className="text-gray-700 truncate flex-1 mr-2" title={sku.name}>
                                  {index + 1}. {sku.name.length > 25 ? sku.name.substring(0, 25) + '...' : sku.name}
                                </span>
                                <span className="font-semibold text-gray-900 text-nowrap">{formatCurrency(sku.revenue)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Top Stores */}
                    <div 
                      className="bg-white border border-green-200 rounded-lg p-3 cursor-pointer hover:border-green-300 hover:shadow-md transition-all duration-200"
                      onClick={() => setShowTopStores(!showTopStores)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <MapPin className="w-4 h-4 text-green-600" />
                        <ChevronDown className={`w-3.5 h-3.5 text-green-600 transition-transform duration-200 ${showTopStores ? 'rotate-180' : ''}`} />
                      </div>
                      <div className="text-2xl font-bold text-gray-900 mb-1">
                        {formatNumber(kpis?.storeCount || 0)}
                      </div>
                      <div className="text-xs text-gray-600 mb-1">Top Stores</div>
                      <div className="text-xs text-green-600 font-medium">
                        Active locations
                      </div>
                      
                      {/* Expandable Top Stores */}
                      {showTopStores && (
                        <div className="mt-3 pt-3 border-t border-green-100">
                          <div className="text-xs font-semibold text-green-700 mb-2">TOP STORES</div>
                          <div className="space-y-1.5">
                            {(topStoreLocations as any)?.stores?.slice(0, 3).map((store: any, index: number) => (
                              <div key={store.name} className="flex items-center justify-between text-xs">
                                <span className="text-gray-700 truncate flex-1 mr-2" title={store.name}>
                                  {index + 1}. {store.name.length > 25 ? store.name.substring(0, 25) + '...' : store.name}
                                </span>
                                <span className="font-semibold text-gray-900 text-nowrap">{formatCurrency(store.revenue)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </section>

        {/* Full AI Insights Panel - Detailed View */}
        <AIInsightsPanel filterByRetailer={true} />

        {/* Analysis & Deep Dives Section */}
        <section className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          {/* Section Header Bar */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 px-4 py-2.5">
            <div className="flex items-center space-x-2">
              <BarChart3 className="w-4 h-4 text-blue-600" />
              <h2 className="text-base font-bold text-gray-900">Analysis & Deep Dives</h2>
            </div>
          </div>

          {/* Content Area */}
          <div className="p-4">
            {/* Analysis Tiles Grid - 3 tiles in one row */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {/* Inventory Coverage Tile */}
              <InventoryCoverageTile />

              {/* Top Revenue Concentration Tile */}
              <TopRevenueConcentrationTile />

              {/* Store Out-of-Stock Tile */}
              <StoreOOSTile />
            </div>
          </div>
        </section>

      </div>
      {/* End Main Content Container */}

      {/* KPI Detail Modals */}
      {activeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-foreground">
                {activeModal === 'revenue' && 'Total Revenue Breakdown'}
                {activeModal === 'units' && 'Units Sold Breakdown'}
                {activeModal === 'skus' && 'Active SKUs Performance'}
                {activeModal === 'stores' && 'Top Store Locations'}
              </h2>
              <button
                onClick={() => setActiveModal(null)}
                className="p-1 hover:bg-secondary rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="space-y-4">
              {activeModal === 'revenue' && (
                <>
                  <div className="text-sm text-muted-foreground mb-4">
                    Top 5 products by total revenue (online + store combined)
                  </div>
                  <div className="space-y-3">
                    {revenueDetail?.map((product, index) => (
                      <div key={product.name} className="bg-secondary/30 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className="bg-primary/20 text-primary px-2 py-1 rounded text-sm font-medium">
                              #{index + 1}
                            </span>
                            <span className="font-medium text-foreground">{product.name}</span>
                          </div>
                          <span className="text-lg font-bold text-foreground">
                            {formatCurrency(product.totalRevenue)}
                          </span>
                        </div>
                        {/* Only show Store/Online breakdown if retailer provides split */}
                        {product.onlineRevenue > 0 && (
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-muted-foreground">Store: </span>
                              <span className="text-foreground">{formatCurrency(product.storeRevenue)}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Online: </span>
                              <span className="text-foreground">{formatCurrency(product.onlineRevenue)}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {activeModal === 'units' && (
                <>
                  <div className="text-sm text-muted-foreground mb-4">
                    Top 5 products by total units sold (online + store combined)
                  </div>
                  <div className="space-y-3">
                    {unitsDetail?.map((product, index) => (
                      <div key={product.name} className="bg-secondary/30 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded text-sm font-medium">
                              #{index + 1}
                            </span>
                            <span className="font-medium text-foreground">{product.name}</span>
                          </div>
                          <span className="text-lg font-bold text-foreground">
                            {formatNumber(product.totalUnits)} units
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Store: </span>
                            <span className="text-foreground">{formatNumber(product.storeUnits)} units</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Online: </span>
                            <span className="text-foreground">{formatNumber(product.onlineUnits)} units</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {activeModal === 'skus' && (
                <>
                  <div className="text-sm text-muted-foreground mb-4">
                    Top 5 active SKUs by performance
                  </div>
                  <div className="space-y-3">
                    {skusDetail?.map((sku, index) => (
                      <div key={sku.name} className="bg-secondary/30 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className="bg-green-500/20 text-green-400 px-2 py-1 rounded text-sm font-medium">
                              #{index + 1}
                            </span>
                            <span className="font-medium text-foreground">{sku.name}</span>
                          </div>
                          <span className="text-lg font-bold text-foreground">
                            {formatCurrency(sku.revenue)}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Units: </span>
                            <span className="text-foreground">{formatNumber(sku.units)}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Avg Price: </span>
                            <span className="text-foreground">{formatCurrency(sku.avgPrice)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {activeModal === 'stores' && (
                <>
                  <div className="text-sm text-muted-foreground mb-4">
                    Top 5 store locations by revenue
                  </div>
                  <div className="space-y-3">
                    {(topStoreLocations as any)?.stores?.slice(0, 5).map((store: any, index: number) => (
                      <div key={store.name} className="bg-secondary/30 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className="bg-purple-500/20 text-purple-400 px-2 py-1 rounded text-sm font-medium">
                              #{index + 1}
                            </span>
                            <span className="font-medium text-foreground">{store.name}</span>
                          </div>
                          <span className="text-lg font-bold text-foreground">
                            {formatCurrency(store.revenue)}
                          </span>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Store location
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Deep Dive Button */}
              <div className="mt-6 pt-4 border-t border-border">
                <button 
                  onClick={() => navigateToDeepDive(activeModal!)}
                  className="flex items-center space-x-2 text-primary hover:text-primary/80 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    View Complete {activeModal === 'revenue' ? 'Revenue' : activeModal === 'units' ? 'Units' : activeModal === 'skus' ? 'SKU' : 'Store'} Details
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
